/**
 * SetPasswordPage — public page at /set-password?token=...
 *
 * Completes an invite (first password) or a password reset via a one-time link. Peeks at the
 * token (GET /v1/auth/invites/{token} — read-only, never consumes it), shows a live password
 * policy checklist, and submits POST /v1/auth/invites/complete. No auto-login — on success the
 * user is sent to the normal sign-in page.
 */

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  GitPullRequestArrow,
  Loader2,
  XCircle,
} from "lucide-react";

import {
  PasswordPolicyChecklist,
  passwordSatisfiesPolicy,
} from "../components/PasswordPolicyChecklist";
import { useCompleteInvite, useInviteDetails } from "../lib/api-central-mu";
import { encodeCredential } from "../utils/encryption";

const SetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center gap-2 mb-1">
            <GitPullRequestArrow className="w-5 h-5 text-foreground" />
            <h1 className="text-lg font-semibold text-foreground">Migration Utility</h1>
          </div>
          <p className="text-sm text-muted-foreground">Cross-instance data sync</p>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-sm p-6">
          {token ? (
            <SetPasswordCard token={token} />
          ) : (
            <LinkErrorCard message="This link is invalid or has already been used." />
          )}
        </div>
      </div>
    </div>
  );
};

function LinkErrorCard({ message }: { message: string }) {
  return (
    <div className="text-center space-y-3 py-2">
      <XCircle className="w-8 h-8 text-destructive mx-auto" />
      <h2 className="text-base font-semibold text-foreground">Link not usable</h2>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function SetPasswordCard({ token }: { token: string }) {
  const navigate = useNavigate();
  const { data: details, isLoading, isError } = useInviteDetails(token);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { mutate: complete, isPending: submitting } = useCompleteInvite();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !details) {
    return (
      <LinkErrorCard message="Couldn't check this link. Please try again in a moment." />
    );
  }

  if (!details.valid) {
    return (
      <LinkErrorCard
        message={
          details.reason === "EXPIRED"
            ? "This link has expired. Ask your administrator to send a new one."
            : "This link is invalid or has already been used."
        }
      />
    );
  }

  if (done) {
    return (
      <div className="text-center space-y-4 py-2">
        <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Password set</h2>
          <p className="text-sm text-muted-foreground">
            You can now sign in with your new password.
          </p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="w-full py-2.5 px-4 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors"
        >
          Go to sign in
        </button>
      </div>
    );
  }

  const policyOk = passwordSatisfiesPolicy(details.passwordPolicy, password);
  const confirmOk = password.length > 0 && password === confirm;
  const canSubmit = policyOk && confirmOk && !submitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setServerError(null);
    complete(
      { token, password: encodeCredential(password) },
      {
        onSuccess: () => setDone(true),
        onError: (error: any) => {
          setServerError(
            error?.response?.data?.message ||
              "Couldn't set the password. Please try again."
          );
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">
          {details.purpose === "INVITE" ? "Welcome — set your password" : "Choose a new password"}
        </h2>
        {details.purpose === "INVITE" && details.email ? (
          <p className="text-sm text-muted-foreground">
            You've been invited to the Migration Utility as{" "}
            <span className="font-medium text-foreground">{details.email}</span>.
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="new-password" className="block text-sm font-medium text-foreground mb-1.5">
          Password
        </label>
        <div className="relative">
          <input
            id="new-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your new password"
            autoComplete="new-password"
            className="w-full px-3 py-2.5 pr-10 text-sm bg-background text-foreground border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none transition-all placeholder:text-muted-foreground"
            disabled={submitting}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <PasswordPolicyChecklist policy={details.passwordPolicy} password={password} />

      <div>
        <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground mb-1.5">
          Confirm password
        </label>
        <div className="relative">
          <input
            id="confirm-password"
            type={showConfirm ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat your new password"
            autoComplete="new-password"
            className="w-full px-3 py-2.5 pr-10 text-sm bg-background text-foreground border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none transition-all placeholder:text-muted-foreground"
            disabled={submitting}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {confirm.length > 0 && !confirmOk ? (
          <p className="mt-1 text-xs text-destructive">Passwords don't match.</p>
        ) : null}
      </div>

      {serverError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {serverError}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full py-2.5 px-4 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
        ) : (
          "Set password"
        )}
      </button>
    </form>
  );
}

export default SetPasswordPage;
